import React, {useState} from 'react';
import {Button, Form, Modal, Overlay, Spinner, Tooltip} from "react-bootstrap";
import DatePicker from 'react-date-picker';
import moment from 'moment';
import {ReactComponent as CalendarIcon} from '../../../assets/icons/calendar.svg';
import {CreatedPartnerCredential, PartnerCredentialForm} from '../../Partners/models';
import { PartnerCredentialService } from '../PartnerCredentials.service';

interface CreatePartnerCredentialModalProps {
    partnerId: number;
    clientId: string;
    handleSubmitClose: (created: CreatedPartnerCredential) => void;
    handleRejectClose: () => void;
}

type CopyKey = 'clientId' | 'secret';

export function CreatePartnerCredentialModal(props: CreatePartnerCredentialModalProps) {
    const [saving, setSaving] = useState(false);
    const [savingError, setSavingError] = useState(false);
    const [validated, setValidated] = useState(false);
    const [name, setName] = useState('');
    const [expiresAt, setExpiresAt] = useState<Date>();
    const [created, setCreated] = useState<CreatedPartnerCredential>();
    const [copiedKey, setCopiedKey] = useState<CopyKey | null>(null);
    const clientIdBtnRef = React.useRef<HTMLButtonElement>(null);
    const secretBtnRef = React.useRef<HTMLButtonElement>(null);

    const handleSubmit = async () => {
        if (!name.trim()) {
            setValidated(true);
            return;
        }

        setSaving(true);
        setSavingError(false);

        const form: PartnerCredentialForm = {
            name: name.trim(),
            expiresAt: expiresAt ? moment(expiresAt).format('YYYY-MM-DD') : undefined,
        };

        try {
            const result = await PartnerCredentialService.createPartnerCredential(props.partnerId, form);
            setCreated(result);
        } catch (e) {
            console.error('Saving failed', e);
            setSavingError(true);
        } finally {
            setSaving(false);
        }
    };

    const handleCopy = async (value: string, key: CopyKey) => {
        if (!value) return;

        let copied = false;
        try {
            if (navigator.clipboard) {
                await navigator.clipboard.writeText(value);
                copied = true;
            }
        } catch (e) {
            console.error('Clipboard API copy failed, falling back', e);
        }

        if (!copied) {
            try {
                const textarea = document.createElement('textarea');
                textarea.value = value;
                textarea.setAttribute('readonly', '');
                textarea.style.position = 'fixed';
                textarea.style.opacity = '0';
                document.body.appendChild(textarea);
                textarea.select();
                document.execCommand('copy');
                document.body.removeChild(textarea);
                copied = true;
            } catch (e) {
                console.error('Fallback copy failed', e);
            }
        }

        if (copied) {
            setCopiedKey(key);
            setTimeout(() => setCopiedKey(prev => (prev === key ? null : prev)), 1500);
        }
    };

    const handleDone = () => {
        if (created) props.handleSubmitClose(created);
    };

    return (
        <Modal
            show
            centered
            size={created ? 'lg' : undefined}
            backdrop={created ? 'static' : true}
            onHide={created ? handleDone : props.handleRejectClose}
        >
            <Modal.Header>
                <Modal.Title>
                    { created ? 'Ligipääs loodud' : 'Ligipääsu loomine' }
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                { created ? (
                    <div className="m-0">
                        <p className="text-danger">
                            See on ainus kord, kui salajast võtit kuvatakse. Kopeeri ja salvesta see kindlasse kohta.
                        </p>

                        <Form.Group className="row m-0 mt-3 align-items-center">
                            <Form.Label className="col-3 m-0">
                                <span className="text-wrap">Nimi</span>
                            </Form.Label>
                            <div className="col p-0">
                                <Form.Control type="text" value={created.name ?? ''} readOnly/>
                            </div>
                        </Form.Group>

                        <Form.Group className="row m-0 mt-3 align-items-center">
                            <Form.Label className="col-3 m-0">
                                <span className="text-wrap">Client ID</span>
                            </Form.Label>
                            <div className="col p-0">
                                <div className="input-group">
                                    <Form.Control
                                        type="text"
                                        value={props.clientId}
                                        readOnly
                                        style={{fontFamily: 'monospace'}}
                                        onFocus={(e: any) => e.target.select()}
                                    />
                                    <div className="input-group-append">
                                        <button
                                            ref={clientIdBtnRef}
                                            className="btn btn-primary rounded d-flex align-items-center"
                                            type="button"
                                            onClick={() => handleCopy(props.clientId, 'clientId')}
                                            title="Kopeeri"
                                        >
                                            <i className="icon icon--16 icon__copy d-inline-block"/>
                                        </button>
                                        <Overlay target={clientIdBtnRef.current} show={copiedKey === 'clientId'} placement="top">
                                            {(overlayProps) => <Tooltip id="copied-clientId" {...overlayProps}>Kopeeritud</Tooltip>}
                                        </Overlay>
                                    </div>
                                </div>
                            </div>
                        </Form.Group>

                        <Form.Group className="row m-0 mt-3 align-items-center">
                            <Form.Label className="col-3 m-0">
                                <span className="text-wrap">Client secret</span>
                            </Form.Label>
                            <div className="col p-0">
                                <div className="input-group">
                                    <Form.Control
                                        type="text"
                                        value={created.secret}
                                        readOnly
                                        style={{fontFamily: 'monospace'}}
                                        onFocus={(e: any) => e.target.select()}
                                    />
                                    <div className="input-group-append">
                                        <button
                                            ref={secretBtnRef}
                                            className="btn btn-primary rounded d-flex align-items-center"
                                            type="button"
                                            onClick={() => handleCopy(created.secret, 'secret')}
                                            title="Kopeeri"
                                        >
                                            <i className="icon icon--16 icon__copy d-inline-block"/>
                                        </button>
                                        <Overlay target={secretBtnRef.current} show={copiedKey === 'secret'} placement="top">
                                            {(overlayProps) => <Tooltip id="copied-secret" {...overlayProps}>Kopeeritud</Tooltip>}
                                        </Overlay>
                                    </div>
                                </div>
                            </div>
                        </Form.Group>
                    </div>
                ) : (
                    <Form
                        noValidate
                        validated={validated}
                        onSubmit={(e: any) => e.preventDefault()}
                        id={'partner-credential-form'}
                        className={'mt-1 align-self-center m-0'}
                    >
                        <Form.Group controlId={'form-partner-credential-name'} className="row m-0">
                            <Form.Label className={'col-5'}>
                                <span className="text-wrap">Nimi*</span>
                            </Form.Label>
                            <div className="col p-0">
                                <Form.Control
                                    required
                                    minLength={1}
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    disabled={saving}
                                    type="text"
                                    isInvalid={validated && !name.trim()}
                                />
                                <Form.Control.Feedback type="invalid">Kohustuslik väli</Form.Control.Feedback>
                            </div>
                        </Form.Group>

                        <Form.Group controlId={'form-partner-credential-expires-at'} className="row m-0 mt-3">
                            <Form.Label className={'col-5'}>
                                <span className="text-wrap">Aegumise kuupäev</span>
                            </Form.Label>
                            <div className="col p-0">
                                <DatePicker
                                    className={'w-100 px-0'}
                                    value={expiresAt}
                                    locale='et'
                                    clearIcon={null}
                                    calendarIcon={<CalendarIcon/>}
                                    format='dd.MM.yyyy'
                                    minDate={moment().add(1, 'day').toDate()}
                                    disabled={saving}
                                    onChange={(event: any) => {
                                        setExpiresAt(event ? new Date(event) : undefined);
                                    }}
                                />
                            </div>
                        </Form.Group>
                    </Form>
                )}
            </Modal.Body>

            <Modal.Footer className="d-flex flex-row align-items-end justify-content-around flex-wrap mb-1">
                { created ? (
                    <Button variant="primary"
                            onClick={handleDone}
                            type="button"
                            className="d-flex align-items-center">
                        <span className='px-2'>Sulge</span>
                    </Button>
                ) : (
                    <>
                        <Button variant="info"
                                onClick={props.handleRejectClose}
                                type="button"
                                disabled={saving}
                                className={"d-flex align-items-center"}>
                            <span className='px-2'>Tühista</span>
                        </Button>
                        <Button variant="primary"
                                onClick={handleSubmit}
                                type="button"
                                disabled={saving}
                                className="d-flex align-items-center">
                            {
                                !saving &&
                                <span className='px-2'>Loo ligipääs</span>
                            }
                            {
                                saving &&
                                <>
                                    <span className='px-2'>Salvestab…</span>
                                    <Spinner size={"sm"} animation={"border"}/>
                                </>
                            }
                        </Button>
                    </>
                )}
            </Modal.Footer>
            {
                !saving && savingError &&
                    <span className="d-flex justify-content-end text-danger px-2 pb-2">
                        Viga salvestamisel, proovi mõne hetke pärast uuesti.
                    </span>
            }
        </Modal>
    );
}
